import {
  BadgeDollarSign,
  BarChart3,
  Bell,
  Bot,
  CircleHelp,
  Headphones,
  CreditCard,
  FileCheck2,
  Gauge,
  Landmark,
  LayoutDashboard,
  RefreshCcw,
  Repeat2,
  RotateCcw,
  Settings,
  ShieldAlert,
  Users,
  WalletCards,
} from "lucide-react";

export const navigation = [
  { label: "Overview", path: "/app", icon: LayoutDashboard },
  { label: "Customer Requests", path: "/app/requests", icon: FileCheck2 },
  { label: "Returns", path: "/app/returns", icon: RotateCcw },
  { label: "Refunds", path: "/app/refunds", icon: WalletCards },
  { label: "Exchanges", path: "/app/exchanges", icon: Repeat2 },
  { label: "Store Credit", path: "/app/store-credit", icon: CreditCard },
  { label: "Fraud", path: "/app/fraud", icon: ShieldAlert },
  { label: "Automation", path: "/app/automation", icon: Bot },
  { label: "Customers", path: "/app/customers", icon: Users },
  { label: "Warranty Claims", path: "/app/warranty", icon: RefreshCcw },
  { label: "Analytics", path: "/app/analytics", icon: BarChart3 },
  { label: "Notifications", path: "/app/notifications", icon: Bell },
  { label: "Billing", path: "/app/billing", icon: BadgeDollarSign },
  { label: "Settings", path: "/app/settings", icon: Settings },
  { label: "Help Center", path: "/app/help", icon: CircleHelp },
  { label: "Support Inbox", path: "/app/support", icon: Headphones },
];

export const operationConfig = {
  "/app/requests": {
    eyebrow: "Customer operations",
    title: "Customer requests",
    description: "Every post-purchase request, coordinated from one queue.",
    action: "Create request",
    tabs: ["All requests", "Needs review", "Assigned to me", "Closed"],
    columns: ["Customer", "Request", "Order", "Amount", "Status", "Risk", "Updated"],
  },
  "/app/returns": {
    eyebrow: "Reverse logistics",
    title: "Returns queue",
    description: "Review return eligibility, reason codes, and package movement.",
    action: "Create return",
    tabs: ["All returns", "Pending approval", "In transit", "Received"],
    columns: ["Customer", "Reason", "Order", "Items", "Status", "Carrier", "Updated"],
  },
  "/app/refunds": {
    eyebrow: "Resolution desk",
    title: "Refunds",
    description: "Approve, automate, and audit every customer refund.",
    action: "Issue refund",
    tabs: ["All refunds", "Awaiting approval", "Automated", "History"],
    columns: ["Customer", "Reason", "Order", "Amount", "Method", "Status", "Updated"],
  },
  "/app/exchanges": {
    eyebrow: "Replacement management",
    title: "Exchanges",
    description: "Match customers to in-stock replacements without losing revenue.",
    action: "Start exchange",
    tabs: ["All exchanges", "Needs selection", "Inventory ready", "Shipped"],
    columns: ["Customer", "Product", "Order", "Replacement", "Status", "Stock", "Updated"],
  },
  "/app/store-credit": {
    eyebrow: "Customer retention",
    title: "Store credit",
    description: "Create flexible resolutions and keep customer value in-store.",
    action: "Issue credit",
    tabs: ["Balances", "Issued", "Redeemed", "Credit history"],
    columns: ["Customer", "Balance", "Issued", "Redeemed", "Expiry", "Status", "Updated"],
  },
  "/app/fraud": {
    eyebrow: "Risk engine",
    title: "Fraud review",
    description: "Prioritize high-risk requests with explainable decision signals.",
    action: "Review alerts",
    tabs: ["Risk inbox", "High risk", "Manual review", "Resolved"],
    columns: ["Customer", "Signal", "Order", "Score", "Recommendation", "Status", "Updated"],
  },
  "/app/customers": {
    eyebrow: "Relationship intelligence",
    title: "Customers",
    description: "Understand lifetime value, order history, and service patterns.",
    action: "Add customer note",
    tabs: ["All customers", "High value", "At risk", "Recent activity"],
    columns: ["Customer", "Lifetime value", "Orders", "Returns", "Risk", "Last order", "Updated"],
  },
  "/app/warranty": {
    eyebrow: "Claims operations",
    title: "Warranty claims",
    description: "Collect evidence, validate coverage, and track replacements.",
    action: "Create claim",
    tabs: ["All claims", "Evidence needed", "In review", "Approved"],
    columns: ["Customer", "Product", "Claim type", "Evidence", "Status", "Owner", "Updated"],
  },
};

export const studioConfig = {
  "/app/automation": {
    icon: Bot,
    eyebrow: "Automation studio",
    title: "Decision workflows",
    description: "Create policy-aware operations that act when your team is offline.",
    action: "New workflow",
    stat: "78%",
    statLabel: "of requests automated",
  },
  "/app/analytics": {
    icon: BarChart3,
    eyebrow: "Performance intelligence",
    title: "Analytics",
    description: "Turn post-purchase activity into retained revenue and better experiences.",
    action: "Export report",
    stat: "$12.4K",
    statLabel: "revenue retained this month",
  },
  "/app/notifications": {
    icon: Bell,
    eyebrow: "Communication center",
    title: "Notifications",
    description: "Keep customers and teammates updated at the moments that matter.",
    action: "Create notification",
    stat: "98.7%",
    statLabel: "delivery success rate",
  },
};

export const settingsSections = [
  "General",
  "Branding",
  "Team management",
  "Roles & permissions",
  "API keys",
  "Webhooks",
  "Integrations",
  "Email templates",
  "Return policy",
  "Refund policy",
  "Store configuration",
  "Workspace settings",
];

export const planData = [
  { name: "Starter", price: "$79", copy: "For early-stage teams", features: ["250 requests", "Email support"] },
  { name: "Growth", price: "$199", copy: "For scaling brands", features: ["2,500 requests", "Automation rules"] },
  { name: "Professional", price: "$499", copy: "For complex operations", features: ["Unlimited requests", "Priority support"] },
  { name: "Enterprise", price: "Custom", copy: "For global commerce", features: ["Custom controls", "Dedicated success"] },
];